import { Link } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import { useEffectiveServiceLines, isLineRuntimeEnabled } from "../hooks/useServiceLines";

/**
 * Route-level gate for a service line.
 *  - If the line is `active` (or `preview` + user has ?preview=true), renders children.
 *  - Otherwise, renders a "Coming Soon" screen so nobody can bypass by typing the URL.
 *
 * Usage:
 *   <Route path="/wizard-crest" element={<ServiceLineGate lineId="wizard"><WizardCrest /></ServiceLineGate>} />
 */
export default function ServiceLineGate({ lineId, children }) {
  const config = useEffectiveServiceLines();
  const line = config?.lines?.find((l) => l.line_id === lineId);

  if (isLineRuntimeEnabled(lineId, config)) {
    return children;
  }

  const name = line?.name || "This service";
  const color = line?.color || "#D4AF37";

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-[#0F172A] via-[#1E293B] to-[#0F172A] flex items-center justify-center px-4"
      data-testid={`service-line-gate-${lineId}`}
    >
      <div className="max-w-md text-center">
        <div
          className="w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center text-4xl"
          style={{ backgroundColor: `${color}20`, border: `1px solid ${color}55` }}
        >
          {line?.icon || <Sparkles className="w-8 h-8" style={{ color }} />}
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-4">
          <span className="text-[#D4AF37] text-xs uppercase tracking-widest font-bold">Coming Soon</span>
        </div>
        <h1 className="font-cinzel text-3xl text-[#F8FAFC] mb-3">
          {name}
        </h1>
        <p className="text-[#94A3B8] font-manrope mb-8">
          This experience is being polished and isn&apos;t live yet. Explore the rest of Happy Ages while we finish up.
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-5 py-3 bg-[#D4AF37] text-[#020617] rounded-lg hover:bg-[#B5952F] font-cinzel tracking-widest"
        >
          Back to home <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
